import {
  UiNode,
  UiNodeImageAttributes,
  UiNodeInputAttributes,
  UiNodeInputAttributesTypeEnum,
  UiNodeTextAttributes,
  UiNodeTypeEnum,
  UiText,
} from '@ory/client';

export enum VKratosUiInputRenderType {
  Button = 'button',
  Input = 'input',
  Image = 'image',
  Text = 'text',
}

/** How the created widget components render their controls. */
export type VKratosUiAppearance = 'default' | 'vx';

export interface VKratosUiWidget {
  id: string;
  group: string;
  renderType: VKratosUiInputRenderType;
  /** Input type; unset for image/text nodes, which have no input semantics. */
  type?: UiNodeInputAttributesTypeEnum;
  attributes: UiNodeInputAttributes;
  label?: UiText;
  messages?: UiText[];
}

export interface VKratosUiInputWidget extends VKratosUiWidget {
  renderType: VKratosUiInputRenderType.Input;
}

export interface VKratosUiButtonWidget extends VKratosUiWidget {
  renderType: VKratosUiInputRenderType.Button;
}

export interface VKratosUiImageWidget extends VKratosUiWidget {
  renderType: VKratosUiInputRenderType.Image;
  imageAttributes: UiNodeImageAttributes;
}

export interface VKratosUiTextWidget extends VKratosUiWidget {
  renderType: VKratosUiInputRenderType.Text;
  textAttributes: UiNodeTextAttributes;
}

const widgetId = (group: string, renderType: string, name: string) =>
  `${group}-${renderType}-${name.replaceAll('.', '-').replaceAll('_', '-')}`;

export const mapUiNodeToUiWidget = (
  node: UiNode
): VKratosUiWidget | undefined => {
  if (node.type === UiNodeTypeEnum.Input) {
    const inputNodeAttributes = node.attributes as UiNodeInputAttributes;
    let renderType = VKratosUiInputRenderType.Input;
    let id = widgetId(node.group, renderType, inputNodeAttributes.name);

    if (
      inputNodeAttributes.type === UiNodeInputAttributesTypeEnum.Submit ||
      inputNodeAttributes.type === UiNodeInputAttributesTypeEnum.Button
    ) {
      renderType = VKratosUiInputRenderType.Button;
      id += '-' + inputNodeAttributes.value;
    }

    return {
      id: id,
      group: node.group,
      renderType: renderType,
      type: inputNodeAttributes.type,
      attributes: inputNodeAttributes,
      label: node.meta.label,
      messages: node.messages,
    };
  }

  // Non-input nodes carry no form control — they only render (the TOTP QR
  // image and secret-key text of the settings flow).
  if (node.type === UiNodeTypeEnum.Img) {
    const imageAttributes = node.attributes as UiNodeImageAttributes;
    return {
      id: widgetId(node.group, 'image', imageAttributes.id),
      group: node.group,
      renderType: VKratosUiInputRenderType.Image,
      attributes: node.attributes as UiNodeInputAttributes,
      imageAttributes,
      label: node.meta.label,
      messages: node.messages,
    } as VKratosUiImageWidget;
  }

  if (node.type === UiNodeTypeEnum.Text) {
    const textAttributes = node.attributes as UiNodeTextAttributes;
    return {
      id: widgetId(node.group, 'text', textAttributes.id),
      group: node.group,
      renderType: VKratosUiInputRenderType.Text,
      attributes: node.attributes as UiNodeInputAttributes,
      textAttributes,
      label: node.meta.label,
      messages: node.messages,
    } as VKratosUiTextWidget;
  }

  return;
};
